// Nine Lives '84 - Clementine's room.
//
// Six rows of valentine hearts climb to Clementine on her ledge at the top.
// Cupids at the corners shoot arrows that ricochet about the room and
// turn whole hearts broken (you fall through) and broken hearts whole
// again. Six of her brothers, one to a row, shadow Tabs' every move
// and knock him down a row on contact; a gift box, picked up from a heart,
// bribes the next brother he meets to leave him alone for a while. Fall to
// the floor and it is back to the alley, another room to clear before she
// will see him again. Reach her and kiss her: an extra life, and the next
// level.

import { type Cat } from '../cat';
import {
  ARROW_H,
  ARROW_LIFE,
  ARROW_W,
  BROTHER_COOLDOWN,
  CAT_H,
  CAT_W,
  DROP_TICKS,
  DT,
  GIFT_H,
  GIFT_W,
  HEART_H,
  HEART_W,
  MAX_GIFTS,
  ROW_TOPS,
  WORLD_H,
  WORLD_W,
} from '../constants';
import { overlaps } from '../physics';
import { Room, type RoomContext } from '../room';
import type { Box, GameEvent, Platform, RoomKind } from '../types';

export interface Heart extends Platform {
  row: number;
  whole: boolean;
}

export interface Brother extends Box {
  row: number;
  facing: 1 | -1;
  /** Ticks left ignoring Tabs after a bribe. */
  bribed: number;
  cooldown: number;
}

export interface Cupid extends Box {
  side: 1 | -1;
  timer: number;
}

export interface Arrow extends Box {
  vx: number;
  vy: number;
  life: number;
  /** The last heart toggled, so one pass toggles it once. */
  last: Heart | null;
}

export interface Gift extends Box {
  heart: Heart;
}

export class FeliciaRoom extends Room {
  readonly kind: RoomKind = 'felicia';
  readonly taskName = 'row';
  readonly hearts: Heart[] = [];
  readonly rows: Heart[][] = [];
  readonly ledge: Platform = { kind: 'ledge', x: 132, y: 181, w: 56, h: 3, oneWay: true };
  readonly felicia: Box = { x: 153, y: 184, w: CAT_W, h: CAT_H };
  readonly brothers: Brother[] = [];
  readonly cupids: Cupid[] = [];
  arrows: Arrow[] = [];
  gifts: Gift[] = [];
  /** Gift boxes Tabs is holding. */
  held = 0;
  failed = false;
  kissed = false;
  private giftTimer: number;

  constructor(ctx: RoomContext) {
    super(ctx);
    // No exit window, stool or sill here: Tabs leaves by the floor or the kiss.
    this.platforms.length = 0;
    this.window.y = -1000;
    this.sill.y = -1000;
    this.broom.x = -1000;
    this.broom.y = -1000;

    ROW_TOPS.forEach((top, row) => {
      const hearts: Heart[] = [];
      const count = row % 2 === 0 ? 6 : 5;
      const x0 = row % 2 === 0 ? 20 : 45;
      for (let i = 0; i < count; i++) {
        hearts.push({
          kind: 'heart',
          x: x0 + i * 50,
          y: top - HEART_H,
          w: HEART_W,
          h: HEART_H,
          oneWay: true,
          row,
          whole: true,
        });
      }
      this.rows.push(hearts);
      this.hearts.push(...hearts);
      // Brothers guard the rows nearest Clementine first; easier settings have fewer.
      if (row >= ROW_TOPS.length - ctx.params.brothers) {
        const rx = ctx.rng.int(x0, x0 + (count - 1) * 50);
        this.brothers.push({
          x: rx,
          y: top,
          w: CAT_W,
          h: CAT_H,
          row,
          facing: -1,
          bribed: 0,
          cooldown: 0,
        });
      }
    });
    this.platforms.push(...this.hearts, this.ledge);
    // The first arrows wait about half a normal interval, so an easy setting
    // gives a few seconds' grace before anything flies.
    const [lo, hi] = ctx.params.cupidEvery;
    this.cupids.push(
      { x: 28, y: 172, w: 12, h: 14, side: 1, timer: ctx.rng.int(lo / 2, hi / 2) },
      { x: WORLD_W - 40, y: 172, w: 12, h: 14, side: -1, timer: ctx.rng.int(lo * 0.75, hi * 0.75) },
    );
    this.giftTimer = ctx.rng.int(...ctx.params.giftEvery);
  }

  get need(): number {
    return ROW_TOPS.length;
  }

  /** The brother guarding a row, if that row has one. */
  brotherOn(row: number): Brother | null {
    return this.brothers.find((b) => b.row === row) ?? null;
  }

  /** The row Tabs is on (1-based), or 0 on the floor. */
  get have(): number {
    return this.rowOf(this.catY);
  }

  private catY = 0;

  private rowOf(y: number): number {
    let row = 0;
    ROW_TOPS.forEach((top, i) => {
      if (y >= top - 0.5) row = i + 1;
    });
    return row;
  }

  protected override get broomActive(): boolean {
    return false;
  }

  /** Tabs starts on the leftmost heart of the bottom row. */
  override spawn(cat: Cat): void {
    const h = this.rows[0][0];
    cat.x = h.x + h.w / 2 - cat.w / 2;
    cat.y = h.y + h.h;
    cat.vx = 0;
    cat.vy = 0;
    cat.onGround = true;
    cat.ground = h;
    cat.facing = 1;
    cat.dropTicks = 0;
    cat.knockedTicks = 0;
    this.catY = cat.y;
  }

  /** Only whole hearts hold weight. */
  private refreshPlatforms(): void {
    this.platforms.length = 0;
    for (const h of this.hearts) if (h.whole) this.platforms.push(h);
    this.platforms.push(this.ledge);
  }

  protected update(cat: Cat, _keys: number, events: GameEvent[]): void {
    this.catY = cat.y;
    if (cat.onGround && cat.ground === null && cat.y <= 0) {
      this.failed = true;
      events.push({ kind: 'fallOut' });
      return;
    }
    if (overlaps(cat, this.felicia)) {
      this.kissed = true;
      this.done = true;
      events.push({ kind: 'kiss', level: 0, lives: 0, bonus: 0 }); // filled in by the game
      return;
    }
    this.updateCupids(events);
    this.updateArrows(events);
    this.updateBrothers(cat, events);
    this.updateGifts(cat, events);
  }

  private updateCupids(_events: GameEvent[]): void {
    const p = this.ctx.params;
    for (const c of this.cupids) {
      if (--c.timer > 0) continue;
      c.timer = this.ctx.rng.int(...p.cupidEvery);
      const angle = (0.35 + this.ctx.rng.next() * 0.35) * Math.PI; // steep-ish diagonals
      this.arrows.push({
        x: c.x + c.w / 2,
        y: c.y,
        w: ARROW_W,
        h: ARROW_H,
        vx: c.side * Math.abs(Math.cos(angle)) * p.arrowSpeed,
        vy: -Math.abs(Math.sin(angle)) * p.arrowSpeed,
        life: ARROW_LIFE,
        last: null,
      });
    }
  }

  private updateArrows(events: GameEvent[]): void {
    let changed = false;
    this.arrows = this.arrows.filter((a) => {
      a.x += a.vx * DT;
      a.y += a.vy * DT;
      if (a.x <= 0 || a.x + a.w >= WORLD_W) {
        a.vx = -a.vx;
        a.x = Math.max(0, Math.min(WORLD_W - a.w, a.x));
      }
      if (a.y <= 0) {
        a.y = 0;
        a.vy = -a.vy;
      } else if (a.y + a.h >= WORLD_H - 10) {
        a.y = WORLD_H - 10 - a.h;
        a.vy = -a.vy;
      }
      for (const h of this.hearts) {
        if (h === a.last) continue;
        if (overlaps(a, { x: h.x, y: h.y - 2, w: h.w, h: h.h + 4 })) {
          h.whole = !h.whole;
          a.last = h;
          changed = true;
          events.push({ kind: h.whole ? 'heartMended' : 'heartBroken' });
          break;
        }
      }
      return --a.life > 0;
    });
    if (changed) {
      this.refreshPlatforms();
      // Gifts on a heart that just broke fall away.
      this.gifts = this.gifts.filter((g) => g.heart.whole);
    }
  }

  private updateBrothers(cat: Cat, events: GameEvent[]): void {
    const speed = this.ctx.params.brotherSpeed;
    const catCx = cat.x + cat.w / 2;
    for (const b of this.brothers) {
      if (b.cooldown > 0) b.cooldown--;
      if (b.bribed > 0) {
        b.bribed--;
        continue;
      }
      const row = this.rows[b.row];
      const lo = row[0].x;
      const hi = row[row.length - 1].x + HEART_W - b.w;
      const dx = catCx - (b.x + b.w / 2);
      const step = speed * DT;
      if (Math.abs(dx) > 1) {
        b.facing = dx > 0 ? 1 : -1;
        b.x += Math.abs(dx) <= step ? dx : b.facing * step;
      }
      b.x = Math.max(lo, Math.min(hi, b.x));
      const onRow = cat.onGround && cat.ground !== null && (cat.ground as Heart).row === b.row;
      if (onRow && b.cooldown === 0 && cat.knockedTicks === 0 && overlaps(cat, b)) {
        b.cooldown = BROTHER_COOLDOWN;
        if (this.held > 0) {
          this.held--;
          b.bribed = this.ctx.params.bribeTicks;
          events.push({ kind: 'bribe' });
        } else {
          // Down a row: shoved sideways and dropped through this one.
          cat.vx = -b.facing * 50;
          cat.vy = -10;
          cat.onGround = false;
          cat.ground = null;
          cat.dropTicks = DROP_TICKS + 2;
          cat.knockedTicks = 30;
          cat.coyote = 0;
          events.push({ kind: 'knocked', by: 'brother' });
        }
      }
    }
  }

  private updateGifts(cat: Cat, events: GameEvent[]): void {
    if (--this.giftTimer <= 0) {
      this.giftTimer = this.ctx.rng.int(...this.ctx.params.giftEvery);
      if (this.gifts.length < 2) {
        const whole = this.hearts.filter((h) => h.whole && h.row < ROW_TOPS.length - 1);
        const h = whole[this.ctx.rng.int(0, whole.length - 1)];
        this.gifts.push({ x: h.x + h.w / 2 - GIFT_W / 2, y: h.y + h.h, w: GIFT_W, h: GIFT_H, heart: h });
      }
    }
    this.gifts = this.gifts.filter((g) => {
      if (this.held < MAX_GIFTS && overlaps(cat, g)) {
        this.held++;
        events.push({ kind: 'gift', held: this.held });
        return false;
      }
      return true;
    });
  }
}
